import React from 'react';

/* ============================================================
   Sequester CCS - Pilot Presentation slide content
   Each slide: { title, body, slideClass?, motif? }
   Authored to fit ~1160×600 at scale 1; the deck auto-scales to
   the viewport so no slide ever needs scrolling. Content split
   across more slides where a single screen would overflow.
   ============================================================ */

export const d = (i) => ({ '--d': `${(i * 0.07).toFixed(2)}s` });

export function SlideHead({ kicker, title, lede }) {
  return (
    <header className="s-head reveal">
      {kicker && <span className="s-kicker">{kicker}</span>}
      <h2 className="s-title">{title}</h2>
      {lede && <p className="s-lede">{lede}</p>}
    </header>
  );
}

export function Hero({ value, label, center, delay = 3 }) {
  return (
    <div className={`s-hero reveal ${center ? 'center' : ''}`} style={d(delay)}>
      <div className="s-hero-num gradient-text">{value}</div>
      <div className="s-hero-label">{label}</div>
    </div>
  );
}

export function DataTable({ cols, rows, accent = true, compact = false, delay = 2 }) {
  return (
    <div className="s-table-wrap reveal" style={d(delay)}>
      <table className={`s-table ${compact ? 'compact' : ''} ${accent ? 's-table--accent' : ''}`}>
        {cols && <thead><tr>{cols.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>}
        <tbody>
          {rows.map((row, i) => {
            const cells = Array.isArray(row) ? row : row.cells;
            const total = !Array.isArray(row) && row.total;
            return (
              <tr key={i} className={total ? 'is-total' : ''}>
                {cells.map((c, j) => <td key={j}>{c}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export const Motif = ({ children }) => <div className="motif" aria-hidden="true">{children}</div>;
export const Pulse = ({ className = '' }) => (
  <div className={`injection-pulse ${className}`}><span /><span /><span /><span /></div>
);

const Defs = () => (
  <defs>
    <linearGradient id="pgrad" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#3b82f6" />
      <stop offset="100%" stopColor="#8b5cf6" />
    </linearGradient>
    <marker id="ah" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <path d="M0,0 L6,3 L0,6 Z" className="svg-arrowhead" />
    </marker>
    <marker id="ahc" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <path d="M0,0 L6,3 L0,6 Z" className="svg-arrowhead-co2" />
    </marker>
  </defs>
);

/* ---------------- visuals ---------------- */

function ValueChain() {
  const nodes = ['Emitter', 'Condition', 'Filter', 'Liquefy', 'Transport', 'Inject', 'Store', 'Revenue'];
  return (
    <div className="reveal" style={{ ...d(1), display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'stretch', justifyContent: 'center' }}>
      {nodes.map((n, i) => (
        <React.Fragment key={n}>
          <div style={{
            flex: '1 1 96px', minWidth: 90, background: '#111827',
            border: '1.5px solid ' + (i === nodes.length - 1 ? '#8b5cf6' : '#3b82f6'),
            borderRadius: 10, padding: '12px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#3b82f6', marginBottom: 3 }}>{String(i + 1).padStart(2, '0')}</div>
            <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#fff' }}>{n}</div>
          </div>
          {i < nodes.length - 1 && <div style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontWeight: 700 }}>→</div>}
        </React.Fragment>
      ))}
    </div>
  );
}

function MembraneSchematic() {
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 920 272" role="img" aria-label="Two-stage membrane process schematic">
        <Defs />
        <line x1="10" y1="120" x2="120" y2="120" className="svg-stream" markerEnd="url(#ah)" />
        <text x="12" y="108" className="svg-sub">Flue gas ~15–25% CO₂</text>
        <rect x="120" y="92" width="96" height="56" rx="8" className="svg-node" />
        <text x="168" y="116" className="svg-label" textAnchor="middle">Condition</text>
        <text x="168" y="132" className="svg-sub" textAnchor="middle">~60 °C / steam</text>
        <line x1="216" y1="120" x2="300" y2="120" className="svg-stream" markerEnd="url(#ah)" />
        <rect x="300" y="70" width="120" height="100" rx="8" className="svg-node" />
        <text x="360" y="60" className="svg-label" textAnchor="middle">Stage 1 Membrane</text>
        {[80, 95, 110, 125, 145].map((y) => <line key={y} x1="312" y1={y} x2="408" y2={y} stroke="#3b82f6" strokeOpacity="0.35" />)}
        <line x1="360" y1="170" x2="360" y2="222" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <text x="368" y="202" className="svg-sub" fill="#3b82f6">vacuum</text>
        <text x="360" y="242" className="svg-label" textAnchor="middle" fill="#3b82f6">~65% CO₂</text>
        <line x1="420" y1="95" x2="470" y2="62" className="svg-stream" markerEnd="url(#ah)" />
        <text x="474" y="60" className="svg-sub">N₂ / O₂ vent</text>
        <path d="M360 247 L520 247 L520 120 L560 120" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <rect x="560" y="60" width="130" height="120" rx="8" className="svg-node" />
        <text x="625" y="50" className="svg-label" textAnchor="middle">Stage 2 Membrane</text>
        {[72, 88, 104, 120, 136, 158].map((y) => <line key={y} x1="572" y1={y} x2="678" y2={y} stroke="#3b82f6" strokeOpacity="0.4" />)}
        <line x1="625" y1="180" x2="625" y2="232" stroke="#8b5cf6" strokeWidth="2.4" markerEnd="url(#ahc)" />
        <text x="633" y="212" className="svg-sub" fill="#8b5cf6">vacuum</text>
        <line x1="690" y1="90" x2="740" y2="62" className="svg-stream" markerEnd="url(#ah)" />
        <text x="744" y="60" className="svg-sub">recycle / vent</text>
        <path d="M625 250 L820 250" stroke="#8b5cf6" strokeWidth="3" fill="none" markerEnd="url(#ahc)" />
        <text x="822" y="246" className="svg-label" fill="#8b5cf6">95% CO₂</text>
        <text x="822" y="262" className="svg-sub">injection-ready</text>
      </svg>
    </div>
  );
}

function PurityBars() {
  return (
    <div className="purity reveal" style={d(2)}>
      {[['Feed gas', '~20%', '#6b7280', '20%'], ['After Stage 1', '~65%', '#3b82f6', '65%'], ['After Stage 2', '~95%', null, '95%']].map(([l, v, c, w]) => (
        <div className="purity-row" key={l}>
          <div className="purity-label">{l}</div>
          <div className="purity-track">
            <div className="purity-bar" style={{ '--pw': w, background: c || '#8b5cf6' }}>{v}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PID() {
  const eq = (x, y, w, h, tag, name) => (
    <g key={tag}>
      <rect x={x} y={y} width={w} height={h} rx="6" className="svg-node" />
      <text x={x + w / 2} y={y + h / 2 - 2} className="svg-tag" textAnchor="middle">{tag}</text>
      <text x={x + w / 2} y={y + h / 2 + 11} className="svg-sub" textAnchor="middle">{name}</text>
    </g>
  );
  const instr = (cx, cy, tag) => (
    <g key={`${tag}-${cx}-${cy}`}>
      <circle cx={cx} cy={cy} r="13" className="svg-instr" />
      <text x={cx} y={cy + 3} className="svg-tag" textAnchor="middle">{tag}</text>
    </g>
  );
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 1180 430" role="img" aria-label="Process and instrumentation diagram">
        <Defs />
        <line x1="20" y1="150" x2="60" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        <text x="20" y="138" className="svg-sub">S-01 Flue gas</text>
        {eq(60, 122, 70, 56, 'E-101', 'Pre-filter')}
        <line x1="130" y1="150" x2="160" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        {eq(160, 122, 70, 56, 'E-103', 'Steam inj.')}
        {eq(160, 220, 70, 46, 'E-102', 'Steam gen')}
        <line x1="195" y1="220" x2="195" y2="178" className="svg-stream" markerEnd="url(#ah)" />
        <line x1="230" y1="150" x2="260" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        {eq(260, 122, 70, 56, 'E-104', 'GCU')}
        {instr(295, 96, 'TIC')}
        <line x1="295" y1="109" x2="295" y2="122" stroke="#9ca3af" strokeDasharray="3 3" />
        <line x1="330" y1="150" x2="360" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        <circle cx="378" cy="150" r="17" className="svg-node" />
        <text x="378" y="153" className="svg-tag" textAnchor="middle">B-101</text>
        <line x1="395" y1="150" x2="420" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        <rect x="420" y="110" width="92" height="84" rx="6" className="svg-node" />
        <text x="466" y="148" className="svg-tag" textAnchor="middle">M-101</text>
        <text x="466" y="161" className="svg-sub" textAnchor="middle">Stage 1</text>
        {[122, 134, 146, 170, 182].map((y) => <line key={y} x1="430" y1={y} x2="502" y2={y} stroke="#3b82f6" strokeOpacity="0.3" />)}
        <line x1="466" y1="110" x2="466" y2="80" className="svg-stream" markerEnd="url(#ah)" />
        <text x="472" y="78" className="svg-sub">S-04 vent</text>
        <line x1="466" y1="194" x2="466" y2="250" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <circle cx="466" cy="270" r="17" className="svg-node-grad" />
        <text x="466" y="273" className="svg-tag" textAnchor="middle">P-101</text>
        {instr(430, 230, 'AI')}
        <text x="404" y="234" className="svg-sub" textAnchor="end">65%</text>
        <path d="M483 270 L600 270 L600 152 L640 152" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <rect x="640" y="110" width="92" height="84" rx="6" className="svg-node" />
        <text x="686" y="148" className="svg-tag" textAnchor="middle">M-102</text>
        <text x="686" y="161" className="svg-sub" textAnchor="middle">Stage 2</text>
        {[122, 134, 146, 158, 170, 182].map((y) => <line key={y} x1="650" y1={y} x2="722" y2={y} stroke="#3b82f6" strokeOpacity="0.35" />)}
        <line x1="686" y1="110" x2="686" y2="80" className="svg-stream" markerEnd="url(#ah)" />
        <text x="692" y="78" className="svg-sub">S-07 recycle</text>
        <line x1="686" y1="194" x2="686" y2="250" stroke="#8b5cf6" strokeWidth="2.4" markerEnd="url(#ahc)" />
        <circle cx="686" cy="270" r="17" className="svg-node-grad" />
        <text x="686" y="273" className="svg-tag" textAnchor="middle">P-102</text>
        {instr(650, 230, 'AI')}
        <text x="624" y="234" className="svg-sub" textAnchor="end">95%</text>
        <path d="M703 270 L770 270 L770 152 L800 152" stroke="#8b5cf6" strokeWidth="2.4" fill="none" markerEnd="url(#ahc)" />
        {eq(800, 124, 64, 56, 'V-101', 'Buffer')}
        <line x1="864" y1="152" x2="890" y2="152" stroke="#8b5cf6" strokeWidth="2.4" markerEnd="url(#ahc)" />
        {eq(890, 124, 70, 56, 'C-101', 'Liquefy')}
        <line x1="960" y1="152" x2="986" y2="152" stroke="#8b5cf6" strokeWidth="2.4" markerEnd="url(#ahc)" />
        {eq(986, 124, 66, 56, 'T-101', 'LCO₂ tank')}
        <line x1="1052" y1="152" x2="1078" y2="152" stroke="#8b5cf6" strokeWidth="2.4" markerEnd="url(#ahc)" />
        {eq(1078, 124, 80, 56, 'TK-101', 'Load-out')}
        <text x="1118" y="200" className="svg-sub" textAnchor="middle" fill="#8b5cf6">S-10 → tanker</text>
        <line x1="466" y1="287" x2="466" y2="300" stroke="#3b82f6" strokeDasharray="3 3" />
        <text x="466" y="316" className="svg-sub" textAnchor="middle">PIC-102</text>
        <line x1="686" y1="287" x2="686" y2="300" stroke="#8b5cf6" strokeDasharray="3 3" />
        <text x="686" y="316" className="svg-sub" textAnchor="middle">PIC-104</text>
        <g transform="translate(20,348)">
          <rect x="0" y="0" width="300" height="78" rx="8" fill="#111827" stroke="#3b82f6" strokeOpacity="0.4" />
          <text x="14" y="20" className="svg-label">Legend</text>
          <line x1="14" y1="36" x2="44" y2="36" className="svg-stream-co2" />
          <text x="52" y="40" className="svg-sub">CO₂ product stream (S-05 → S-10)</text>
          <line x1="14" y1="54" x2="44" y2="54" className="svg-stream" />
          <text x="52" y="58" className="svg-sub">Retentate / vent / utility</text>
          <circle cx="29" cy="72" r="8" className="svg-instr" />
          <text x="52" y="76" className="svg-sub">Instrument bubble</text>
        </g>
      </svg>
    </div>
  );
}

function WabashMap() {
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 560 380" role="img" aria-label="Wabash County site map">
        <Defs />
        <rect x="40" y="30" width="480" height="370" rx="10" fill="#6b7280" fillOpacity="0.08" stroke="#6b7280" strokeOpacity="0.3" />
        {[0.18, 0.34, 0.5, 0.66].map((t, i) => <ellipse key={i} cx="300" cy="225" rx={60 + i * 55} ry={42 + i * 38} fill="none" stroke="#6b7280" strokeOpacity="0.15" />)}
        <polygon points="200,140 360,130 380,275 230,305 180,225" fill="#3b82f6" fillOpacity="0.08" stroke="#3b82f6" strokeWidth="1.5" />
        <text x="280" y="208" className="svg-label" textAnchor="middle">Wabash County, IL</text>
        <g transform="translate(285,242)">
          <circle r="9" fill="none" stroke="#3b82f6" strokeWidth="2" />
          <line x1="-14" y1="0" x2="14" y2="0" stroke="#3b82f6" strokeWidth="1.5" />
          <line x1="0" y1="-14" x2="0" y2="14" stroke="#3b82f6" strokeWidth="1.5" />
          <text x="0" y="30" className="svg-sub" textAnchor="middle">3 wells</text>
        </g>
        <line x1="150" y1="110" x2="276" y2="235" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="6 5" markerEnd="url(#ahc)" />
        <text x="178" y="166" className="svg-sub" fill="#3b82f6">~8 km</text>
        {[[140, 100], [120, 190], [410, 110], [430, 250], [200, 350]].map(([x, y], i) => (
          <g key={i} transform={`translate(${x},${y})`}>
            <polygon points="0,-11 9,-5 9,5 0,11 -9,5 -9,-5" fill="#111827" stroke="#3b82f6" strokeWidth="1.4" />
            <circle cx="0" cy="0" r="2.5" fill="#3b82f6" />
          </g>
        ))}
        <text x="56" y="90" className="svg-sub">5 coal mines · 1.2 Mtpa ea</text>
        <g transform="translate(420,350)">
          <polygon points="0,-13 12,9 -12,9" fill="#111827" stroke="#3b82f6" strokeWidth="1.6" />
          <text x="0" y="26" className="svg-sub" textAnchor="middle">Power · 9 Mtpa</text>
        </g>
      </svg>
    </div>
  );
}

export function GeologyCrossSection({ operational }) {
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 900 420" role="img" aria-label="Subsurface cross-section">
        <Defs />
        <rect x="0" y="0" width="900" height="70" fill="#0a0a0a" />
        <rect x="0" y="66" width="900" height="6" fill="#374151" />
        <rect x="0" y="72" width="900" height="70" className="svg-strata-a" />
        <rect x="0" y="142" width="900" height="60" className="svg-strata-b" />
        <rect x="0" y="202" width="900" height="40" className="svg-strata-c" />
        <rect x="0" y="242" width="900" height="22" fill="#475569" />
        <text x="16" y="257" className="svg-sub">Impermeable caprock (seal)</text>
        <rect x="0" y="264" width="900" height="110" className="svg-reservoir" />
        <text x="16" y="284" className="svg-label" fill="#93c5fd">Depleted reservoir, sub-hydrostatic</text>
        {[290, 305, 320, 335, 350, 365].map((y) => <line key={y} x1="0" y1={y} x2="900" y2={y} stroke="#3b82f6" strokeOpacity="0.12" />)}
        <rect x="0" y="374" width="900" height="46" fill="#1f2937" />
        {[230, 450, 670].map((x, i) => {
          const producer = operational && i === 2; // right-hand well produces oil
          return (
            <g key={i}>
              <rect x={x - 3} y="40" width="6" height="270" fill="#9ca3af" />
              <rect x={x - 12} y="26" width="24" height="16" rx="3" className="svg-node" />
              {[14, 26, 38].map((r) => <circle key={r} cx={x} cy="320" r={r} fill="none" stroke="#3b82f6" strokeOpacity={0.5 - r / 100} />)}
              {producer
                ? <line x1={x} y1="300" x2={x} y2="44" stroke="#8b5cf6" strokeWidth="2.6" markerEnd="url(#ahc)" />
                : <line x1={x} y1="44" x2={x} y2="300" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#ahc)" />}
            </g>
          );
        })}
        <text x="450" y="20" className="svg-label" textAnchor="middle" fill="#3b82f6">CO₂ injection ↓</text>
        {operational && <text x="610" y="20" className="svg-sub" fill="#8b5cf6">Oil mobilised ↑</text>}
      </svg>
    </div>
  );
}

function SitePlan() {
  const skid = (x, y, w, label) => (
    <g key={label + x}>
      <rect x={x} y={y} width={w} height="40" rx="5" className="svg-node" />
      <text x={x + w / 2} y={y + 24} className="svg-tag" textAnchor="middle">{label}</text>
    </g>
  );
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 920 286" role="img" aria-label="Capture site layout">
        <Defs />
        <rect x="20" y="55" width="150" height="195" rx="8" fill="#6b7280" fillOpacity="0.12" stroke="#6b7280" strokeOpacity="0.5" />
        <text x="95" y="150" className="svg-sub" textAnchor="middle">Coal mine</text>
        <text x="95" y="166" className="svg-sub" textAnchor="middle">emitter</text>
        <line x1="170" y1="150" x2="230" y2="150" className="svg-stream" markerEnd="url(#ah)" />
        <text x="172" y="140" className="svg-sub">flue header</text>
        <rect x="230" y="38" width="640" height="226" rx="10" fill="#3b82f6" fillOpacity="0.04" stroke="#3b82f6" strokeOpacity="0.35" strokeDasharray="5 4" />
        <text x="250" y="60" className="svg-sub">Capture unit pad, ~20 m × 15 m</text>
        {skid(250, 78, 80, 'E-101')}
        {skid(345, 78, 90, 'E-102/103')}
        {skid(450, 78, 80, 'E-104')}
        {skid(250, 148, 110, 'M-101 / M-102')}
        {skid(375, 148, 100, 'P-101 / P-102')}
        {skid(490, 148, 80, 'C-101')}
        {skid(620, 93, 90, 'T-101')}
        {skid(620, 173, 100, 'TK-101')}
        <line x1="570" y1="168" x2="620" y2="118" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <line x1="665" y1="133" x2="665" y2="173" className="svg-stream-co2" markerEnd="url(#ahc)" />
        <g transform="translate(760,193)">
          <rect x="0" y="0" width="70" height="26" rx="6" className="svg-node-grad" />
          <circle cx="16" cy="30" r="5" fill="#9ca3af" />
          <circle cx="54" cy="30" r="5" fill="#9ca3af" />
          <text x="35" y="17" className="svg-tag" textAnchor="middle">tanker</text>
        </g>
        <text x="795" y="245" className="svg-sub" textAnchor="middle" fill="#8b5cf6">TO TRANSPORT →</text>
      </svg>
    </div>
  );
}

function TransportRoute() {
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 760 220" role="img" aria-label="Transport route">
        <Defs />
        <g transform="translate(70,110)">
          <polygon points="0,-26 22,-13 22,13 0,26 -22,13 -22,-13" fill="#111827" stroke="#3b82f6" strokeWidth="1.6" />
          <text x="0" y="4" className="svg-tag" textAnchor="middle">capture</text>
          <text x="0" y="50" className="svg-sub" textAnchor="middle">TK-101 load-out</text>
        </g>
        <line x1="100" y1="110" x2="660" y2="110" stroke="#3b82f6" strokeWidth="2.4" strokeDasharray="9 7" />
        <text x="380" y="92" className="svg-label" textAnchor="middle">~8 km · ~20–25 min · no pipeline</text>
        <g transform="translate(370,110)">
          <rect x="-34" y="-13" width="68" height="26" rx="7" className="svg-node-grad" />
          <circle cx="-18" cy="16" r="5" fill="#9ca3af" />
          <circle cx="18" cy="16" r="5" fill="#9ca3af" />
        </g>
        <g transform="translate(690,110)">
          <circle r="14" fill="none" stroke="#3b82f6" strokeWidth="2" />
          <line x1="-22" y1="0" x2="22" y2="0" stroke="#3b82f6" strokeWidth="1.5" />
          <line x1="0" y1="-22" x2="0" y2="22" stroke="#3b82f6" strokeWidth="1.5" />
          <text x="0" y="52" className="svg-sub" textAnchor="middle">wellsite V-201</text>
        </g>
      </svg>
    </div>
  );
}

function PaybackCurve() {
  const pts = [];
  for (let m = 0; m <= 36; m++) {
    const x = 70 + (m / 36) * 740;
    const y = 290 - ((1.68 / 12) * m / 5) * 230;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  const capexY = 290 - (2.7 / 5) * 230;
  return (
    <div className="s-visual reveal" style={d(1)}>
      <svg viewBox="0 0 860 330" role="img" aria-label="Payback curve">
        <Defs />
        <line x1="70" y1="290" x2="810" y2="290" stroke="#374151" />
        <line x1="70" y1="50" x2="70" y2="290" stroke="#374151" />
        <line x1="70" y1={capexY} x2="810" y2={capexY} stroke="#6b7280" strokeDasharray="5 5" />
        <text x="76" y={capexY - 8} className="svg-sub">Capex $2.7m</text>
        <polyline points={pts.join(' ')} fill="none" stroke="#3b82f6" strokeWidth="2.5" />
        <circle cx={70 + (19 / 36) * 740} cy={capexY} r="6" fill="#8b5cf6" />
        <text x={70 + (19 / 36) * 740} y={capexY - 14} textAnchor="middle" fill="#8b5cf6" style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>Payback · mo 19</text>
        {[0, 12, 19, 24, 36].map((m) => <text key={m} x={70 + (m / 36) * 740} y="308" className="svg-sub" textAnchor="middle">{m}</text>)}
        <text x="440" y="326" className="svg-sub" textAnchor="middle">Months</text>
        <text x="76" y="46" className="svg-sub">Cumulative revenue ($m)</text>
      </svg>
    </div>
  );
}

export function WaterfallChart({ maxWidth = 660 }) {
  const steps = [
    { name: '45Q credit', delta: 85, kind: 'start' },
    { name: 'Emitter fee', delta: 1, kind: 'rise' },
    { name: 'Filtration', delta: -20, kind: 'fall' },
    { name: 'Compression', delta: -11, kind: 'fall' },
    { name: 'Injection', delta: -1, kind: 'fall' },
    { name: 'Gross profit', kind: 'total' },
  ];
  const base = 296, top = 40, maxV = 92, scale = (base - top) / maxV;
  const yOf = (v) => base - v * scale;
  const bw = 66, gap = 46, x0 = 86;
  let cum = 0;
  const bars = steps.map((s, i) => {
    const x = x0 + i * (bw + gap);
    let yTop, h, fill, label, level;
    if (s.kind === 'start') { cum = s.delta; level = cum; yTop = yOf(cum); h = cum * scale; fill = '#3b82f6'; label = `+$${s.delta}`; }
    else if (s.kind === 'total') { level = cum; yTop = yOf(cum); h = cum * scale; fill = '#f5f6f7'; label = `$${cum}`; }
    else { const after = cum + s.delta; const hi = Math.max(cum, after), lo = Math.min(cum, after); yTop = yOf(hi); h = Math.max((hi - lo) * scale, 3); fill = s.delta >= 0 ? '#60a5fa' : '#6b7280'; label = (s.delta > 0 ? '+$' : '−$') + Math.abs(s.delta); cum = after; level = cum; }
    return { x, yTop, h, fill, label, name: s.name, level, kind: s.kind };
  });
  return (
    <div className="s-visual reveal" style={{ ...d(1), maxWidth, margin: '0 auto' }}>
      <svg viewBox="0 0 760 350" role="img" aria-label="Cost waterfall: $85 revenue to $54 gross profit">
        <Defs />
        {[0, 25, 50, 75].map((v) => (
          <g key={v}>
            <line x1="70" y1={yOf(v)} x2="730" y2={yOf(v)} stroke="#ffffff" strokeOpacity="0.06" />
            <text x="62" y={yOf(v) + 3} className="svg-sub" textAnchor="end">${v}</text>
          </g>
        ))}
        <line x1="70" y1={base} x2="730" y2={base} stroke="#374151" />
        {bars.slice(0, -1).map((b, i) => (
          <line key={i} x1={b.x + bw} y1={yOf(b.level)} x2={bars[i + 1].x} y2={yOf(b.level)} stroke="#6b7280" strokeOpacity="0.6" strokeDasharray="4 4" />
        ))}
        {bars.map((b) => {
          const big = b.kind === 'start' || b.kind === 'total';
          return (
            <g key={b.name}>
              <rect x={b.x} y={b.yTop} width={bw} height={b.h} rx="3" fill={b.fill} />
              <text x={b.x + bw / 2} y={b.yTop - (big ? 12 : 8)} textAnchor="middle" fill={b.kind === 'fall' ? '#9ca3af' : '#fff'}
                style={{ fontSize: big ? 19 : 13, fontWeight: big ? 700 : 600, fontFamily: 'Inter, sans-serif' }}>{b.label}</text>
              <text x={b.x + bw / 2} y={base + 18} className="svg-sub" textAnchor="middle">{b.name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function DonutMargin() {
  return (
    <div className="donut-wrap reveal" style={d(2)}>
      <div className="donut">
        <svg viewBox="0 0 120 120" width="210" height="210">
          <Defs />
          <circle cx="60" cy="60" r="50" fill="none" stroke="#6b7280" strokeOpacity="0.3" strokeWidth="12" />
          <circle cx="60" cy="60" r="50" fill="none" stroke="#3b82f6" strokeWidth="12" strokeDasharray={`${(63.5 / 100) * 314} 314`} strokeLinecap="round" />
        </svg>
        <div className="donut-center"><b className="gradient-text">63.5%</b></div>
      </div>
      <div className="purity-label">gross margin</div>
    </div>
  );
}

/* ============================================================ SLIDES */

const slides = [
  /* 1 - TITLE */
  {
    title: 'Title',
    slideClass: 'title-slide',
    motif: () => <Motif><div className="reservoir-grid" /><Pulse className="top" /></Motif>,
    body: () => (
      <>
        <div className="title-wordmark reveal gradient-text">SEQUESTER CCS</div>
        <div className="title-tag reveal" style={d(1)}>Permanent Carbon Storage. Proven Technology. Immediate Revenue.</div>
        <div className="title-rule reveal" style={d(2)} />
        <div className="title-meta reveal" style={d(3)}>
          <div className="sub">Illinois Pilot Project: Investor Briefing</div>
          <div>July 2026</div>
        </div>
        <p className="title-quote reveal" style={d(4)}>
          “We take CO₂ from industrial emitters, store it permanently in depleted oil reservoirs, and earn $85 for every tonne under US law.”
        </p>
      </>
    ),
  },

  /* 2 - THE PROBLEM */
  {
    title: 'The Problem',
    motif: () => <Motif><div className="carbon-stream" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="The Problem" title="37 Billion Tonnes of CO₂. Almost None Captured."
          lede="Carbon capture is essential and the technology exists. Deployment economics keep it stuck at demonstration scale, leaving a ~$630bn/year opportunity untapped." />
        <div className="s-split lean-visual">
          <div className="reveal" style={d(1)}>
            <div className="bars">
              <div className="bar-col"><div className="bar-fill blue" style={{ '--bh': '3%' }}><span className="bar-val">50</span></div><div className="bar-name">Current CCS<br />50 Mtpa</div></div>
              <div className="bar-col"><div className="bar-fill grad" style={{ '--bh': '100%' }}><span className="bar-val">7,600</span></div><div className="bar-name">2050 IPCC target<br />7,600 Mtpa</div></div>
            </div>
          </div>
          <DataTable delay={2} rows={[
            ['Global CO₂ emissions / yr', '37 billion tonnes'],
            ['Current CCS capacity', '50 million tonnes / yr'],
            ['Gap: target vs reality', '99.9% uncaptured'],
            ['Net Zero needs (2050)', '7.6 billion t / yr'],
            ['Trajectory', '152× scale-up in 26 yrs'],
          ]} />
        </div>
        <Hero value="99.9%" label="of global CO₂ emissions currently go uncaptured" delay={3} />
      </>
    ),
  },

  /* 3 - WHAT SEQUESTER DOES */
  {
    title: 'What Sequester Does',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="What We Do" title="CO₂ as a Resource, Not a Problem"
          lede="Every link is standard industrial practice. Natural resource extraction, in reverse." />
        <ValueChain />
        <Hero value="$85/tonne" label="US 45Q tax credit, written into law" delay={3} center />
      </>
    ),
  },

  /* 4 - VALUE CHAIN STAGES */
  {
    title: 'The Value Chain, Stage by Stage',
    body: () => (
      <>
        <SlideHead kicker="What We Do" title="Eight Stages, One Asset" sm />
        <div className="s-split">
          <DataTable delay={1} compact cols={['Stage', 'Function']} rows={[
            ['Emitter', 'Industrial CO₂ source: coal mine, power station, cement'],
            ['Condition', 'Steam injection to ~60 °C and correct humidity for the membranes'],
            ['Filter', 'Two-stage Aqualung membranes: 65% → 95% pure CO₂'],
            ['Liquefy', 'Cryogenic liquefaction on-site for tanker transport'],
          ]} />
          <DataTable delay={2} compact cols={['Stage', 'Function']} rows={[
            ['Transport', 'Cryogenic road tanker to wellsite, ~8 km'],
            ['Inject', 'Boil-off, recompression and injection via standard wells'],
            ['Store', 'Permanently trapped in porous rock at sub-hydrostatic pressure'],
            ['Revenue', '$85/t 45Q credit + oil production uplift from EOR'],
          ]} />
        </div>
      </>
    ),
  },

  /* 5 - AQUALUNG PROCESS */
  {
    title: 'The Aqualung Process',
    body: () => (
      <>
        <SlideHead kicker="How It Works" title="Two Stages. Vacuum-Driven. 95% Pure CO₂." sm
          lede="A partial-pressure differential pulls CO₂ across each membrane. No high feed pressure." />
        <div className="s-split lean-visual">
          <MembraneSchematic />
          <div>
            <PurityBars />
            <Hero value="95% Pure" label="CO₂ output from the two-stage membrane process" delay={3} />
          </div>
        </div>
      </>
    ),
  },

  /* 6 - P&ID DIAGRAM */
  {
    title: 'Process & Instrumentation Diagram',
    body: () => (
      <>
        <SlideHead kicker="Engineering" title="Full P&ID: Flue Gas Intake to 95% CO₂" sm
          lede="The complete capture unit as deployed at the Wabash County pilot: equipment, instrumentation, control loops and CO₂ product streams (blue)." />
        <PID />
      </>
    ),
  },

  /* 7 - P&ID EQUIPMENT */
  {
    title: 'P&ID - Major Equipment',
    body: () => (
      <>
        <SlideHead kicker="Engineering" title="Major Equipment Items" sm />
        <DataTable delay={1} compact cols={['Tag', 'Equipment', 'Function']} rows={[
          ['E-101', 'Pre-filter / Scrubber', 'Removes dust, particulates and moisture from raw flue gas'],
          ['E-102/103', 'Steam Generator / Injector', 'Produces and inline-injects process steam for conditioning'],
          ['E-104', 'Gas Conditioning Unit', 'Temperature & humidity trim to precise Stage 1 inlet conditions'],
          ['B-101', 'Feed Blower', 'Low-differential positive pressure on the Stage 1 feed side'],
          ['M-101 / P-101', 'Stage 1 module + vacuum pump', 'Selective permeation: mixed stream → ~65% CO₂ (PIC-102)'],
          ['M-102 / P-102', 'Stage 2 module + vacuum pump', '~65% → ~95% CO₂ (PIC-104)'],
          ['V-101 / C-101', 'Buffer + Cryo liquefaction', 'Buffers product, then condenses to liquid at ~ −20 °C / 20 bar'],
          ['T-101 / TK-101', 'Storage tank + loading skid', 'Cryogenic storage; custody-transfer metered load-out for 45Q'],
        ]} />
      </>
    ),
  },

  /* 8 - P&ID LOOPS & STREAMS */
  {
    title: 'P&ID - Control Loops & Streams',
    body: () => (
      <>
        <SlideHead kicker="Engineering" title="Control Loops & Stream Compositions" sm />
        <div className="s-section-label reveal" style={d(1)}>Key control loops</div>
        <DataTable delay={1} compact cols={['Loop', 'Controlled variable', 'Manipulated variable']} rows={[
          ['TIC-102 Temperature', 'Post-GCU temperature (~60 °C)', 'Steam injection rate (E-102/103)'],
          ['PIC-102 / PIC-104 Vacuum', 'M-101 / M-102 permeate pressure', 'P-101 / P-102 pump speed (VFD)'],
          ['LIC-101 Tank level', 'T-101 cryogenic level', 'C-101 rate / TK-101 dispatch'],
        ]} />
        <div className="s-section-label reveal" style={{ ...d(2), marginTop: 18 }}>Stream compositions</div>
        <DataTable delay={2} compact cols={['Stream', 'Description', 'Approx. composition']} rows={[
          ['S-01', 'Flue gas inlet', '15–25% CO₂, 65–75% N₂, 5–10% O₂, 2–5% H₂O'],
          ['S-05', 'Stage 1 permeate', '~65% CO₂, ~20% N₂, ~10% O₂'],
          ['S-08', 'Stage 2 permeate (product)', '~95% CO₂, <4% N₂, trace O₂/Ar'],
          ['S-10', 'Tanker load-out', 'Liquid CO₂; metered (FI-104 / AI-103) for 45Q'],
        ]} />
      </>
    ),
  },

  /* 9 - THE PILOT PROJECT */
  {
    title: 'The Pilot Project',
    motif: () => <Motif><div className="formation-map" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="The Pilot" title="Wabash County, Illinois: The Proving Ground"
          lede="Three existing wells in one of the densest industrial CO₂ clusters in the US Midwest." />
        <div className="s-split">
          <WabashMap />
          <div>
            <DataTable delay={2} compact rows={[
              ['Asset', 'Option on oil-producing lease'],
              ['Existing wells', '3 wells, ~7 → ~25 bbl/day via EOR'],
              ['Pilot target', '12,500 tonnes CO₂/year'],
              ['Nearby emitters', '5 coal mines + 1 power station'],
              ['Transport', '~8 km road, no pipeline'],
            ]} />
            <Hero value="15 Mtpa" label="CO₂ within 10 km; the pilot targets just 12,500 tpa (0.08%)" delay={3} />
          </div>
        </div>
      </>
    ),
  },

  /* 10 - GEOLOGY */
  {
    title: 'Site Overview & Geology',
    body: () => (
      <>
        <SlideHead kicker="Geology" title="Three Wells. Known Reservoirs. Minimal Risk." sm
          lede="The reservoir held hydrocarbons for millions of years. Containment is already proven." />
        <div className="s-split lean-visual">
          <GeologyCrossSection />
          <div>
            <Hero value="Minimal" label="geological risk: containment proven by decades of production history" delay={2} />
            <ul className="s-points reveal" style={{ ...d(3), marginTop: 18 }}>
              <li><strong>Containment</strong> proven by the oil the rock held, not by a model</li>
              <li><strong>Geometry and capacity</strong> known from decades of well records</li>
              <li><strong>Wells, roads and surface kit</strong> already in place</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },

  /* 11 - GEOLOGY DETAIL */
  {
    title: 'Why Depleted Reservoirs',
    body: () => (
      <>
        <SlideHead kicker="Geology" title="Built for CO₂, Cheaper to Drill" sm />
        <div className="s-split">
          <DataTable delay={1} compact cols={['Property', 'Significance']} rows={[
            ['Proven containment', 'Held hydrocarbons for millions of years'],
            ['Known geometry', 'Extent, depth & capacity from production history'],
            ['Existing infra', 'Wells, surface kit and road access in place'],
            ['Regulatory precedent', 'Depleted-reservoir injection is permitted practice'],
            ['EOR bonus', 'CO₂ mobilises residual oil: ~7 → ~25 bbl/day'],
          ]} />
          <div>
            <DataTable delay={2} compact cols={['Well type', 'Casing', 'Cost']} rows={[
              ['Standard CCS well', '5-string', 'High'],
              ['Sequester depleted-reservoir', '2–3 string', 'Much lower'],
            ]} />
            <p className="s-note reveal" style={d(3)}>
              The reservoir is already proven, so the risk profile is orders of magnitude lower: fewer casing strings, materially less well cost.
            </p>
          </div>
        </div>
      </>
    ),
  },

  /* 12 - CAPTURE SITE */
  {
    title: 'Pilot Execution: Capture Site',
    motif: () => <Motif><div className="carbon-stream" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Execution · Phase 1" title="Mine to Membrane"
          lede="A modular skid assembly on a ~20 m × 15 m pad beside the emitter, capturing CO₂ at the coal face." />
        <div className="s-split lean-visual">
          <SitePlan />
          <div>
            <DataTable delay={2} compact cols={['Parameter', 'Target']} rows={[
              ['CO₂ capture rate', '~40–50 tonnes/day design capacity'],
              ['Annualised capture', '12,500 tonnes/year'],
              ['Output', 'Liquid CO₂, tanker-ready on site'],
            ]} />
            <Hero value="40–50 t/day" label="CO₂ captured at the coal face, 12,500 tonnes per year" delay={3} />
          </div>
        </div>
      </>
    ),
  },

  /* 13 - TRANSPORT */
  {
    title: 'Pilot Execution: Transport',
    motif: () => <Motif><div className="formation-map" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Execution · Phase 2" title="8 km. No Pipeline. Cryogenic Road Transport." sm
          lede="Liquid CO₂ takes up ~500× less volume than gas. A standard cryogenic tanker does the job." />
        <div className="s-split">
          <TransportRoute />
          <DataTable delay={2} compact cols={['Parameter', 'Value']} rows={[
            ['Transport state', 'Liquid CO₂, ~ −20 °C, ~20 bar'],
            ['Tanker', 'Cryogenic ADR/DOT, ~20–25 t/load'],
            ['Trips per day', '2–3 at pilot rate'],
            ['Distance', '~8 km, ~20–25 min/trip'],
            ['Scale-up', '9 Mtpa → dedicated pipeline'],
          ]} />
        </div>
        <Hero value="$0" label="Pipeline capex at pilot stage: road transport replaces $3.2m/km infrastructure" delay={3} />
      </>
    ),
  },

  /* 14 - INJECTION */
  {
    title: 'Pilot Execution: Injection & Storage',
    body: () => (
      <>
        <SlideHead kicker="Execution · Phase 3" title="Sub-Hydrostatic. Gravity-Assisted. EOR." sm
          lede="Decades of extraction left the reservoir under-pressured. It is asking to be filled." />
        <div className="s-split lean-visual">
          <GeologyCrossSection operational />
          <div>
            <Hero value="25 bbl/day" label="Post-injection oil, up from 7. EOR plus permanent storage from one well." delay={2} />
            <ul className="s-points reveal" style={{ ...d(3), marginTop: 18 }}>
              <li><strong>Sub-hydrostatic:</strong> the pressure gradient assists injection</li>
              <li><strong>Residual oil mobilised:</strong> ~7 to ~25 bbl/day</li>
              <li><strong>Metered injection</strong> feeds the 45Q audit trail</li>
            </ul>
          </div>
        </div>
      </>
    ),
  },

  /* 15 - INJECTION DETAIL */
  {
    title: 'Sub-Hydrostatic Pressure & EOR',
    body: () => (
      <>
        <SlideHead kicker="Execution · Phase 3" title="The Pressure Gradient Works for Us" sm />
        <div className="s-split">
          <div>
            <div className="s-section-label reveal" style={d(1)}>Injection energy by reservoir type</div>
            <DataTable delay={1} compact cols={['Reservoir', 'Energy required']} rows={[
              ['New / virgin (≥ hydrostatic)', 'High compression'],
              ['Standard depleted', 'Moderate compression'],
              ['Sub-hydrostatic (Wabash)', 'Minimal, gravity-assisted'],
            ]} />
          </div>
          <div>
            <div className="s-section-label reveal" style={d(2)}>Enhanced Oil Recovery</div>
            <DataTable delay={2} compact cols={['EOR parameter', 'Value']} rows={[
              ['Pre-injection', '~7 bbl/day'],
              ['Post-CO₂-flood', '~25 bbl/day'],
              ['Uplift', '+18 bbl/day (+257%)'],
              ['Oil price', '$68/barrel'],
              ['Annual oil revenue', '~$620,500'],
            ]} />
          </div>
        </div>
      </>
    ),
  },

  /* 16 - PERMANENT STORAGE */
  {
    title: 'Permanent Storage',
    body: () => (
      <>
        <SlideHead kicker="Execution · Phase 3" title="Five Trapping Mechanisms" sm
          lede="Structural and stratigraphic trapping give immediate permanent containment; residual, solubility and mineral trapping increase security over time." />
        <DataTable delay={1} compact cols={['Mechanism', 'Description']} rows={[
          ['Structural', 'Impermeable caprock prevents upward migration (primary seal)'],
          ['Stratigraphic', 'Rock-type changes at boundaries limit lateral migration'],
          ['Residual', 'Capillary pressure traps isolated CO₂ bubbles in pore throats'],
          ['Solubility', 'CO₂ dissolves into brine, becomes denser and sinks, not rises'],
          ['Mineralisation', 'Reacts with formation minerals to form stable carbonates'],
        ]} />
      </>
    ),
  },

  /* 17 - TIMELINE */
  {
    title: 'Pilot Timeline & Milestones',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => {
      /* axis ticks (-2, 0, 3, 6, 9, 12, 15, 18) are evenly spaced: each
         interval is 100/7 = 14.29% of track width. Bars derived from the
         stated phase months so the chart agrees with the Month 12 claim. */
      const phases = [
        ['Phase 0 · Pre-Execution', 'Seed close, legal, lease option', 0, 14],
        ['Phase 1 · Permit & Procure', 'UIC permits filed, long-lead equipment', 19, 10],
        ['Phase 2 · Site & Wells', 'Civil works, 3 wells re-completed', 29, 14],
        ['Phase 3 · Install & Tie-In', 'Aqualung skid, liquefaction, compressor', 43, 14],
        ['Phase 4 · Commission & Test', 'N₂ flush, integrity test, MV protocol', 57, 10],
        ['Phase 5 · First Injection', 'First 45Q credit-eligible tonne', 70, 4],
        ['Phase 6 · Ramp-Up', 'Ramp to 12,500 tpa, scale-up package', 71, 29],
      ];
      return (
        <>
          <SlideHead kicker="Timeline" title="12 Months from Seed to First Tonne" sm />
          <div className="gantt reveal" style={d(1)}>
            {phases.map(([label, sub, left, width], i) => (
              <div className="gantt-row" key={label}>
                <div className="gantt-label">{label}<small>{sub}</small></div>
                <div className="gantt-track"><div className="gantt-fill" style={{ left: `${left}%`, width: `${width}%`, transitionDelay: `${0.1 * i}s` }} /></div>
              </div>
            ))}
          </div>
          <div className="gantt-axis reveal" style={d(2)}>{['−2', '0', '3', '6', '9', '12', '15', '18'].map((m) => <span key={m}>{m}</span>)}</div>
          <Hero value="Month 12" label="First tonne in the ground. First 45Q credit eligible. Scale-up financing unlocked." delay={3} />
        </>
      );
    },
  },

  /* 18 - CRITICAL PATH */
  {
    title: 'Critical Path & Milestones',
    body: () => (
      <>
        <SlideHead kicker="Timeline" title="What Drives the Schedule" sm />
        <div className="s-split">
          <div>
            <div className="s-section-label reveal" style={d(1)}>Critical path</div>
            <DataTable delay={1} compact cols={['Item', 'Mitigation']} rows={[
              ['UIC permit', 'Existing producers → faster Class II conversion'],
              ['Aqualung unit (4–6 mo)', 'Order Month 1, parallel to permitting'],
              ['Liquefaction (3–4 mo)', 'Order in Phase 1'],
              ['Emitter supply', 'Pre-agreed Phase 0, formalised Phase 1'],
            ]} />
          </div>
          <div>
            <div className="s-section-label reveal" style={d(2)}>Key milestones</div>
            <DataTable delay={2} compact cols={['Milestone', 'Month']} rows={[
              ['Seed close', '0'],
              ['All permits filed', '3'],
              ['All wells injection-ready', '6'],
              ['Mechanical completion', '9'],
              ['Commissioning complete', '11'],
              ['First CO₂ / first 45Q tonne', '12'],
              ['Full pilot rate', '15–18'],
            ]} />
          </div>
        </div>
      </>
    ),
  },

  /* 19 - UNIT ECONOMICS (waterfall + margin) */
  {
    title: 'Unit Economics',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Economics" title="$54 Gross Profit Per Tonne. At Every Scale."
          lede="Costs are fixed by contract. The $85 credit is US tax law, not a carbon price." />
        <div className="s-split lean-visual">
          <WaterfallChart />
          <DonutMargin />
        </div>
        <Hero value="$54/tonne" label="a 63.5% margin that holds from 12,500 tpa to 9 Mtpa: the licence is fixed and the credit is law" delay={3} center />
      </>
    ),
  },

  /* 21 - REVENUE MODEL */
  {
    title: 'Revenue Model',
    motif: () => <Motif><div className="carbon-stream" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Revenue" title="Two Revenue Streams. One Asset." sm />
        <div className="s-grid s-grid-2">
          <div className="s-card s-card--top reveal" style={d(1)}>
            <div className="eyebrow">Stream 1 · 45Q Carbon Credits</div>
            <div className="big gradient-text">$1.06m</div>
            <p>$85/t statutory rate, 12-year credit term per facility, confirmed in the Big Beautiful Bill. Monetised via elective direct pay in the early years, then credit transfer; verified under EPA Subpart RR metering, already in pilot scope.</p>
          </div>
          <div className="s-card s-card--top reveal" style={d(2)}>
            <div className="eyebrow">Stream 2 · Oil Production (EOR)</div>
            <div className="big gradient-text">$0.62m</div>
            <p>Gross wellhead revenue as output rises ~7 → ~25 bbl/day (+257%) at $68/bbl. A bonus stream, not the model.</p>
          </div>
        </div>
        <Hero value="$1.68m" label="Combined annual revenue from the pilot alone" delay={4} center />
      </>
    ),
  },

  /* 22 - FINANCIALS */
  {
    title: 'Pilot Financials',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Financials" title="$2.7m Capex · 19-Month Revenue Payback" sm />
        <div className="s-split">
          <PaybackCurve />
          <DataTable delay={2} compact cols={['Metric', 'Value']} rows={[
            ['Annual revenue (pilot)', '$1.68m'],
            ['Annual revenue vs capex', '~62%'],
            ['First injection', 'Month 12'],
            ['Purpose', 'Unlock 9 Mtpa scale-up financing'],
          ]} />
        </div>
        <Hero value="19 months" label="of revenue covers the $2.7m seed: the pilot is the proof that unlocks the scale-up" delay={3} />
      </>
    ),
  },

  /* 23 - SCALE-UP */
  {
    title: 'Scale-Up Opportunity',
    motif: () => <Motif><Pulse className="top" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="The Opportunity" title="Pilot Proves the Chain. 9 Mtpa Is the Business." sm />
        <div className="s-grid s-grid-scale">
          <div className="s-card s-card--top reveal" style={d(1)}>
            <div className="eyebrow">Pilot</div>
            <div className="big" style={{ color: '#fff', fontSize: '1.7rem' }}>12,500 tpa</div>
            <p>45Q revenue <strong style={{ color: '#fff' }}>$1.06m/yr</strong> · 12 months · 1× baseline</p>
          </div>
          <div className="scale-x reveal" style={d(2)}>720×</div>
          <div className="s-card s-card--grad reveal" style={d(2)}>
            <div className="eyebrow">Scale-up</div>
            <div className="big gradient-text" style={{ fontSize: '2.2rem' }}>9,000,000 tpa</div>
            <p>45Q revenue <strong className="gradient-text">$630m/yr</strong> net of credit monetisation · EBITDA ~$380m (≈60%)</p>
          </div>
        </div>
        <div className="reveal" style={{ ...d(3), marginTop: 18 }}>
          <div className="purity-label" style={{ marginBottom: 8 }}>Available CO₂ supply - Wabash County alone</div>
          <div style={{ position: 'relative', height: 38, background: 'rgba(107,114,128,0.25)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, width: '60%', background: '#3b82f6', display: 'flex', alignItems: 'center', paddingLeft: 14, fontSize: '0.8rem', fontWeight: 700 }}>9 Mtpa targeted</div>
            <span style={{ position: 'absolute', right: 14, top: 10, fontSize: '0.8rem', color: '#9ca3af' }}>15 Mtpa available</span>
          </div>
        </div>
        <Hero value="$380m" label="Projected EBITDA at 9 Mtpa, unlocked by the $2.7m pilot" delay={4} />
      </>
    ),
  },

  /* 24 - WHY NOW */
  {
    title: 'Why Now / Competitive Advantage',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Why Now" title="The Market Opens. Sequester Is Ready." sm />
        <div className="s-table-wrap reveal" style={d(1)}>
          <table className="s-table compact s-table--win">
            <thead><tr><th>Dimension</th><th>Traditional CCS</th><th>Sequester</th></tr></thead>
            <tbody>
              {[
                ['Geological risk', 'New reservoir, unknown', 'Depleted O&G, proven'],
                ['Well cost', '5-string, expensive', '2–3 string, cheaper'],
                ['Pipeline', '$3.2m/km, often tens of km', 'Under 10 km, no pipeline'],
                ['Regulatory path', 'Novel, lengthy permitting', 'Established O&G operations'],
                ['Technology', 'New / unproven', 'Aqualung: patented, field-proven'],
                ['Revenue basis', 'Variable carbon price', '45Q: statutory $85/t, 12-yr term'],
              ].map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
        <Hero value="6 of 6" label="dimensions where the depleted-reservoir model wins: each one a cost or a risk removed" delay={2} />
      </>
    ),
  },

  /* 24b - RISKS & DE-RISKING */
  {
    title: 'Risks & De-Risking',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Risks" title="We Know Where the Risk Lives" sm
          lede="Every material risk is identified, owned and already being retired." />
        <DataTable delay={1} compact cols={['Risk', 'How it is de-risked']} rows={[
          ['Emitter supply', 'Pre-agreed with the emitter; formalised before commissioning'],
          ['Site control', 'Lease option on the Wabash County wellsite already secured'],
          ['Permitting', 'Existing producer wells convert via the established UIC Class II path'],
          ['Technology dependency', 'Exclusive Aqualung licence; our CEO ran Aqualung’s finances'],
          ['45Q eligibility', 'Design capacity 40–50 t/day gives headroom above the 12,500 tpa credit threshold'],
          ['Execution', 'Long-lead orders placed Month 1, in parallel with permitting'],
        ]} />
      </>
    ),
  },

  /* 25 - TEAM */
  {
    title: 'Team',
    motif: () => <Motif><div className="formation-map" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="Team" title="30 Years in the Ground. Built to Execute." sm
          lede="Every major risk in this project maps to a founder who has solved it before." />
        <div className="s-grid s-grid-3">
          {[
            ['TH', 'Terje Hagevang', 'Co-Founder', '30 years oil & gas: well design, completion and reservoir management. Saw that depleted reservoirs are the key to deployable CCS.'],
            ['FT', 'Frederik Tufte', 'Co-Founder', 'Former CFO of Aqualung Carbon Capture, the technology Sequester licences. Built the Aqualung–Sequester commercial bridge.'],
            ['NS', 'Nathan Scrivens', 'Co-Founder', 'Well engineering at Shell, delivering multi-million-dollar savings on complex, high-stakes wells. Founder of a $300m+ technology company.'],
          ].map(([initials, name, role, bio], i) => (
            <div className="s-card s-card--top reveal" key={name} style={d(i + 1)}>
              <div className="mono">{initials}</div>
              <h4>{name}</h4>
              <div className="role">{role}</div>
              <p>{bio}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },

  /* 26 - THE ASK */
  {
    title: 'The Ask / Next Steps',
    motif: () => <Motif><Pulse className="top" /><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="The Ask" title="$2.7m Seed. 12 Months. Storage at Scale." sm />
        <div className="s-statband reveal" style={d(1)}>
          <div><b>$2.7m</b><span>seed round</span></div>
          <div><b>12 mo</b><span>to first injection</span></div>
          <div><b>$1.68m</b><span>revenue per year</span></div>
          <div><b>19 mo</b><span>revenue payback</span></div>
        </div>
        <div className="s-split">
          <div className="reveal" style={d(2)}>
            <div className="s-section-label">Use of proceeds</div>
            <div className="stack">
              {[['Equipment', '38%', '#1d4ed8', '#fff'], ['Well workover', '24%', '#2563eb', '#fff'], ['Surface infra', '18%', '#3b82f6', '#fff'], ['Permitting', '12%', '#60a5fa', '#0a0a0b'], ['Contingency', '8%', '#93c5fd', '#0a0a0b']].map(([n, w, c, tc]) => (
                <div className="stack-seg" key={n} style={{ flexBasis: w, background: c, color: tc }}>{parseInt(w, 10) >= 12 ? n : ''}</div>
              ))}
            </div>
            <div className="stack-legend">
              {[['Equipment', '#1d4ed8'], ['Well workover', '#2563eb'], ['Surface infra', '#3b82f6'], ['Permitting', '#60a5fa'], ['Contingency', '#93c5fd']].map(([n, c]) => (
                <span key={n}><i style={{ background: c }} />{n}</span>
              ))}
            </div>
          </div>
          <div className="s-card s-card--top contact-card reveal" style={d(3)}>
            <div className="eyebrow">What investors receive</div>
            <p style={{ marginBottom: 14 }}>Equity in Sequester CCS Holding Ltd. First-mover position under the world's most favourable CCS incentive regime. A pilot that underwrites the path to $380m EBITDA.</p>
            <div className="c-name">Nathan Scrivens</div>
            <div className="c-role">Co-Founder</div>
            <p><a href="mailto:ns@sequesterccs.com">ns@sequesterccs.com</a><br /><a href="tel:+447595302565">+44 7595 302 565</a></p>
          </div>
        </div>
      </>
    ),
  },

  /* 27 - ASK MILESTONES */
  {
    title: 'What the Seed Delivers',
    motif: () => <Motif><div className="reservoir-grid" /></Motif>,
    body: () => (
      <>
        <SlideHead kicker="The Ask" title="What the Seed Delivers" sm />
        <DataTable delay={1} compact cols={['Milestone', 'Timeline', 'Outcome']} rows={[
          ['Permitting & site prep', 'Months 1–3', 'Filings submitted, site cleared'],
          ['Equipment procurement', 'Months 1–4', 'Aqualung unit, compression, metering ordered'],
          ['Well workover', 'Months 3–6', 'Three wells injection-ready'],
          ['Full commissioning', 'Months 9–11', 'Integrated system tested, MV protocol active'],
          ['First CO₂ injection', 'Month 12', 'First tonne stored, first 45Q credit eligible'],
          ['Revenue generation', 'Month 12+', '$1.68m/year at pilot rate'],
        ]} />
        <p className="title-meta reveal" style={{ ...d(2), marginTop: 18 }}>Sequester CCS Holding Ltd - June 2026 · ns@sequesterccs.com</p>
      </>
    ),
  },

  /* 28 - APPENDIX A */
  {
    title: 'Appendix A - Key Numbers',
    body: () => (
      <>
        <SlideHead kicker="Appendix A" title="Key Numbers Reference" sm />
        <div className="s-split">
          <DataTable delay={1} compact cols={['Metric', 'Value']} rows={[
            ['Pilot target', '12,500 tpa'],
            ['45Q credit rate', '$85/tonne'],
            ['Annual 45Q revenue', '$1,062,500'],
            ['Annual oil revenue', '~$620,500'],
            ['Total pilot revenue', '~$1,683,000'],
            ['Seed capex', '$2.7m'],
            ['Revenue payback', '~19 months'],
            ['Annual revenue vs capex', '~62%'],
            ['Build timeline', '12 months'],
            ['Gross profit / tonne', '~$54 (63.5%)'],
          ]} />
          <DataTable delay={2} compact cols={['Metric', 'Value']} rows={[
            ['Scale-up target', '9,000,000 tpa'],
            ['Scale-up revenue', '$630m/year net of monetisation'],
            ['Scale-up EBITDA', '$380m/year (≈60%)'],
            ['CO₂ within 10 km', '15 Mtpa'],
            ['Pipeline cost avoided', '$3.2m/km'],
            ['Capture design capacity', '40–50 t CO₂/day'],
            ['Capture rate', '40–50 t/day'],
            ['Stage 1 / 2 purity', '~65% / ~95%'],
            ['Transport state', 'Liquid, ~ −20 °C, ~20 bar'],
            ['EOR uplift', '~7 → ~25 bbl/day'],
          ]} />
        </div>
      </>
    ),
  },

  /* 29 - APPENDIX B (1/2) */
  {
    title: 'Appendix B - Terminology (1/2)',
    body: () => (
      <>
        <SlideHead kicker="Appendix B" title="Terminology (1 / 2)" sm />
        <DataTable delay={1} compact cols={['Term', 'Definition']} rows={[
          ['45Q', '$85/tonne US tax credit for CO₂ permanently stored. IRA, confirmed in the Big Beautiful Bill.'],
          ['CCS', 'Carbon Capture and Storage: capturing CO₂ from a source and storing it permanently.'],
          ['Enhanced Oil Recovery', 'CO₂ injection restores pressure and mobilises residual oil, a secondary revenue stream.'],
          ['Aqualung', 'Norwegian company; patented membrane filtration. Technology partner and equipment supplier.'],
          ['Selective permeation', 'CO₂ crosses the membrane preferentially under a partial-pressure differential.'],
          ['Vacuum-driven permeation', 'A vacuum pump on the permeate side creates the driving force for separation.'],
          ['Cryogenic liquefaction', 'Cooling CO₂ to liquid (~ −20 °C at ~20 bar) for efficient transport.'],
          ['Depleted reservoir', 'A largely-extracted oil/gas reservoir: proven containment, existing wells.'],
        ]} />
      </>
    ),
  },

  /* 30 - APPENDIX B (2/2) */
  {
    title: 'Appendix B - Terminology (2/2)',
    body: () => (
      <>
        <SlideHead kicker="Appendix B" title="Terminology (2 / 2)" sm />
        <DataTable delay={1} compact cols={['Term', 'Definition']} rows={[
          ['Sub-hydrostatic pressure', 'Pore pressure below a full water column at depth; assists CO₂ injection.'],
          ['Supercritical CO₂', 'Above 31 °C and 73.8 bar; dense, fills pore space efficiently at reservoir conditions.'],
          ['Structural trapping', 'Impermeable caprock above the reservoir prevents upward CO₂ migration.'],
          ['Solubility trapping', 'CO₂ dissolves into brine, which becomes denser and sinks.'],
          ['Mineralisation', 'Dissolved CO₂ reacts with minerals to form stable carbonates: permanent fixation.'],
          ['EPA Subpart RR', 'US federal MMV reporting rule for geological sequestration; required for 45Q.'],
          ['UIC Class II / VI', 'Injection-well classifications; Class II is the faster conversion pathway here.'],
          ['Proximity doctrine', 'Site-selection rule: emitter within 10 km of the wellsite, eliminating pipeline cost.'],
        ]} />
      </>
    ),
  },
];

export default slides;
